// String table for UI labels shared by the web-ui SPA and the browser extension.
// Two roles: (1) labels the backend emits as stable keys (e.g. SSE `vm_phase`
// events carry `phase: "warm_adopt"`, never an already-translated sentence) and
// (2) plain UI chrome we want localized once, in one place. The backend stays
// locale-agnostic; the UI owns translation, keyed off the signed-in user's locale.
//
// Layout: `Strings[localeCode][messageKey]`. localeCode is a BCP-47 primary
// subtag ("en", "sv"). `defaultLocale` is the safety net when neither the user's
// locale nor any of its prefixes is supported.

import { defaultLocale, type LocaleCode } from './strings/base';
// Re-export the locale primitives so existing importers (./i18n, the package
// index) keep resolving them from here unchanged.
export { defaultLocale, type LocaleCode };

// ── Per-area string fragments ──
// Each i18n sweep adds one file under strings/ and one import + spread in the
// compose block at the bottom. Fragment contents live in their own modules so
// the table can grow without this index ballooning. (ADD FRAGMENT IMPORTS HERE)
import { agentsStrings } from './strings/agents';
import { skillsStrings } from './strings/skills';
import { appsDocsStrings } from './strings/apps-docs';
import { schedulesNotifStrings } from './strings/schedules-notif';
import { onboardingAuthStrings } from './strings/onboarding-auth';
import { studioMeStrings } from './strings/studio-me';
import { adminCoreStrings } from './strings/admin-core';
import { adminCommsStrings } from './strings/admin-comms';
import { componentsStrings } from './strings/components';
import { componentsMiscStrings } from './strings/components-misc';
import { shellStrings } from './strings/shell';
import { tablesStrings } from './strings/tables';

// The originally-inline table: common + studio + share + more + signin + the
// first extraction wave (ext + home + quickask + conversations + inbox). Newer
// areas live in fragment modules and are spread into `Strings` below.
const base: Record<LocaleCode, Record<string, string>> = {
  // Tone note: status labels are playful gerunds in the spirit of
  // Claude's spinner ("Metamorphosing...", "Unfurling..."), tilted
  // toward HQ's weaving metaphor where it earns its keep. The
  // user reads these dozens of times a day; they should feel alive
  // rather than like a tech progress bar ("Booting a fresh VM").
  en: {
    'studio.pending.default':   'Thinking',
    'studio.pending.early':     'Thinking',
    'studio.pending.mid':       'Working',
    'studio.pending.long':      'Still working',
    'studio.pending.very_long': 'Almost there',

    'studio.phase.starting':    'Starting',
    'studio.phase.acquiring':   'Preparing',
    'studio.phase.warm_adopt':  'Preparing',
    'studio.phase.restoring':   'Resuming',
    'studio.phase.cold_start':  'Starting up',
    'studio.phase.vm_ready':    'Almost ready',

    'studio.tools.calling':     'Using {name}',
    'studio.tools.used_one':    'Used 1 tool',
    'studio.tools.used_other':  'Used {count} tools',
    'studio.tools.copy':        'Copy',
    'studio.tools.copied':      'Copied',
    'studio.tools.label_args':  'Arguments',
    'studio.tools.label_result':'Result',

    'studio.share.button':           'Share',
    'studio.share.button_title':     'Share {slug}',
    'studio.share.title':            'Share this app',
    'studio.share.close':            'Close',
    'studio.share.cancel':           'Cancel',
    'studio.share.copy_link':        'Copy link',
    'studio.share.copied':           'Copied',
    'studio.share.mode_expiring':    'People I invite',
    'studio.share.mode_public':      'Anyone with the link',
    'studio.share.expiring_hint':    'A signed link that lets one person in. Set how long it stays valid.',
    'studio.share.ttl_label':        'Link expires in',
    'studio.share.ttl_1h':           '1 hour',
    'studio.share.ttl_24h':          '24 hours',
    'studio.share.ttl_7d':           '7 days',
    'studio.share.ttl_30d':          '30 days',
    'studio.share.public_warn_title':'Make this app public?',
    'studio.share.public_warn_body': 'Anyone who has the URL will be able to open it - no sign-in, no expiry. You can switch back to invite-only at any time.',
    'studio.share.make_public':      'Make public',
    'studio.share.make_private':     'Switch back to invite-only',
    'studio.share.public_hint':      'Anyone with this link can open the app.',
    'studio.share.clipboard_unavailable': 'Clipboard unavailable - select the URL manually.',

    // Generic words reused across many views. Prefer these over per-view copies.
    'common.back':                   'Back',
    'common.save':                   'Save',
    'common.saving':                 'Saving…',
    'common.cancel':                 'Cancel',
    'common.close':                  'Close',
    'common.delete':                 'Delete',
    'common.remove':                 'Remove',
    'common.edit':                   'Edit',
    'common.loading':                'Loading…',
    'common.retry':                  'Retry',
    'common.confirm':                'Confirm',
    'common.copy':                   'Copy',
    'common.copied':                 'Copied',
    'common.search':                 'Search',
    'common.name':                   'Name',
    'common.description':            'Description',
    'common.enabled':                'Enabled',
    'common.disabled':               'Disabled',
    'common.yes':                    'Yes',
    'common.no':                     'No',
    'common.done':                   'Done',
    'common.next':                   'Next',
    'common.dismiss':                'Dismiss',
    'common.error_generic':          'Something went wrong',
    'mermaid.render_failed':         'This diagram could not be rendered. Showing its source.',

    // Toast notifications (web-ui ToastStack). region_label names the aria-live
    // region for screen readers.
    'toast.region_label':            'Notifications',

    // Extension "More" screen (apps/extension/components/MoreView.vue).
    'more.title':                    'More',
    'more.about.title':              'About me',
    'more.about.sub':                'What HQ remembers about you',
    'more.files.title':              'Files',
    'more.files.sub':                'Documents & generated files',
    'more.apps.title':               'Apps',
    'more.apps.sub':                 'Agent-built apps',
    'more.browser.title':            'Browser control',
    'more.browser.sub':              'Let agents drive this browser',
    'more.selection.title':          'Selection toolbar',
    'more.selection.sub':            'Quick actions when you highlight text on a page',
    'more.web.title':                'Open HQ web',
    'more.web.sub':                  'The full app in your browser',
    'more.signout.title':            'Sign out',
    'more.signout.sub':              'End your session on this device',

    // Sign-in surface (apps/web-ui/src/views/SigninView.vue).
    'signin.title':                  'Sign in to HQ',
    'signin.sub':                    "Use the workspace you have HQ installed in. We don't ask for a separate password.",
    'signin.slack.title':            'Sign in with Slack',
    'signin.slack.redirecting':      'Redirecting…',
    'signin.slack.sub':              'Use your existing workspace identity',
    'signin.teams.title':            'Sign in with Microsoft',
    'signin.teams.sub':              'Use your Microsoft work account',
    'signin.teams.redirecting':      'Redirecting…',
    'signin.divider':                'or with email',
    'signin.email.aria':             'Email address',
    'signin.email.sending':          'Sending…',
    'signin.email.cta':              'Email me a sign-in link',
    'signin.email.sub':              'For guests and invited members without Slack',
    'signin.magic_sent_before':      'If',
    'signin.magic_sent_after':       'has access, a sign-in link is on the way. Check your email.',
    'signin.code.cta':               'Enter your code',
    'signin.code.sub':               'Got the code by email? Type it here to sign in on this device.',
    'signin.code.aria':              'Sign-in code',
    'signin.code.verifying':         'Checking…',
    'signin.code.err':               "That code didn't work. Check your email and try again.",
    'signin.err.slack_start':        'Slack sign-in failed to start',
    'signin.err.teams_start':        'Microsoft sign-in failed to start',
    'signin.err.invalid_email':      'Enter a valid email address.',
    'signin.pw.toggle':              'Sign in with a password',
    'signin.pw.back':                'Back to sign-in options',
    'signin.pw.sub':                 'Enter your account email and password.',
    'signin.pw.email_aria':          'Email address',
    'signin.pw.password_aria':       'Password',
    'signin.pw.password_ph':         'Password',
    'signin.pw.cta':                 'Sign in',
    'signin.pw.signing':             'Signing in…',
    'signin.pw.choose_title':        'Choose a workspace',
    'signin.pw.err.required':        'Enter your email and password.',
    'signin.pw.err.invalid':         'Invalid email or password.',
    'signin.pw.err.throttled':       'Too many sign-in attempts. Please try again later.',
    'signin.aside.eyebrow':          'New to HQ',
    'signin.aside.title':            'Specialists that do the work, not just chat',
    'signin.aside.lede':             '@mention a specialist in Slack or Teams and it researches, decides, and delivers - reports, documents, email, schedules.',
    'signin.aside.point1':           'Lives in Slack and Teams, not another tab',
    'signin.aside.point2':           'Does real work: research, documents, email, schedules',
    'signin.aside.point3':           'Remembers and learns from your work',
    'signin.aside.cta':              'Create a workspace',
    'signin.aside.note':             'Invite required during beta.',

    // ── Extension side panel (apps/extension/entrypoints/sidepanel/App.vue) ──
    'ext.tab.ask':                   'Ask',
    'ext.tab.agents':                'Agents',
    'ext.signin.sub':                'Sign in to your workspace.',
    'ext.signin.button':             'Sign in to HQ',
    'ext.signin.new_before':         'New to HQ?',
    'ext.signin.get_started':        'Get started →',
    'ext.signin.device_title':       'Approve sign-in',
    'ext.signin.device_lede':        'We opened a verification page. Confirm this code there:',
    'ext.signin.device_open':        'Reopen verification page',
    'ext.signin.device_waiting':     'Waiting for approval…',
    'ext.signin.device_cancel':      'Cancel',
    'ext.ws.title':                  'Workspace: {name}',
    'ext.stale.message':             'HQ was updated. Reload to finish.',
    'ext.stale.reload':              'Reload',
    'ext.ask.quick_ask':             'Quick ask',
    'ext.ask.search':                'Search quick asks…',
    'ext.ask.recent':                'Recent',
    'ext.agents.choose':             'Choose an agent',
    'ext.agents.search':             'Search conversations…',
    'ext.agents.choose_agent':       'Choose agent',
    'ext.agents.an_agent':           'an agent',
    'ext.agents.send_to':            'Send to {name}',
    'ext.composer.ask_anything':     'Ask anything…',
    'ext.composer.message_agent':    'Message the agent…',
    'ext.composer.ask_agent':        'Ask {name}…',
    'ext.composer.attach':           'Attach files',
    'ext.composer.uploading':        'uploading…',
    'ext.composer.stop':             'Stop the agent',
    'ext.composer.nudge':            'Nudge',
    'ext.composer.send':             'Send',
    'ext.empty.ask':                 'Ask anything - fast, no sandbox.',
    'ext.empty.ask_agent':           'Ask {name} anything.',
    'ext.empty.send_to_start':       'Send a message to get started.',
    'ext.empty.no_matches':          'No matches.',
    'ext.empty.no_threads':          'No agent threads yet.',
    'ext.loading_more':              'Loading more…',
    'ext.tool.calling_before':       'Calling ',
    'ext.tool.calling_after':        '…',
    'ext.tool.no_details':           'No details.',
    'ext.notice.context_full':       'This quick ask is full.',
    'ext.notice.convert':            'Convert to an agent thread →',
    'ext.drop.thread':               'Drop to attach to this thread',
    'ext.drop.new_thread':           'Drop to attach to a new agent thread',
    'ext.readonly.slack':            'Happening in Slack - reply in the channel.',
    'ext.browser.use':               'Use my browser',
    'ext.browser.acting':            'HQ is acting on the page…',
    'ext.browser.controlling':       'Controlling your live browser - click to stop and disconnect',
    'ext.browser.connecting':        'Connecting to your browser…',
    'ext.browser.enable_hint':       "Let agents control your live browser - connects the extension and runs on the page you're viewing",
    'ext.browser.warn_title':        'HQ can now act in your browser.',
    'ext.browser.warn_body':         "It can read and take actions on the pages you open, which may put your data at risk. Deactivate when you're done.",
    'ext.browser.deactivate':        'Deactivate',
    'ext.confirm.delete_title':      'Delete conversation',
    'ext.confirm.delete_msg':        'Delete "{title}"? This can\'t be undone.',
    'ext.confirm.delete_fallback':   'this conversation',
    'ext.sender.you':                'You',
    'ext.sender.assistant':          'Assistant',
    'ext.err.no_agent':              'No agent selected.',
    'ext.err.upload_failed':         'Upload failed: {error}',
    'ext.err.agent':                 'agent error',
    'ext.cmd.placeholder':           'Search or run a command…',
    'ext.cmd.new_ask':               'New quick ask',
    'ext.cmd.new_thread':            'New agent thread',
    'ext.cmd.browser_on':            'Turn on "Use my browser"',
    'ext.cmd.browser_off':           'Turn off "Use my browser"',
    'ext.cmd.open_studio':           'Open in Studio',
    'ext.cmd.settings':              'Settings',
    'ext.cmd.searching':             'Searching…',
    'ext.cmd.untitled':              'Untitled',
    'ext.cmd.message_agent':         'Message {name}',
    'ext.cmd.group_actions':         'Actions',
    'ext.cmd.group_agents':          'Agents',
    'ext.cmd.group_view':            'View',
    'ext.cmd.group_switch_ws':       'Switch workspace',
    'ext.cmd.group_account':         'Account',
    'ext.cmd.group_conversations':   'Conversations',

    // ── Extension components ──
    'ext.browser.title':             'Let agents control this browser',
    'ext.browser.status_connected':  'Connected',
    'ext.browser.status_connecting': 'Connecting…',
    'ext.browser.status_waiting':    'Waiting to connect',
    'ext.browser.status_off':        'Off',
    'ext.browser.lead':              'An agent you ask can read the page, click, type, and navigate your own logged-in tabs - and only when you ask it to.',
    'ext.browser.note_banner':       'Chrome shows a "debugging this browser" banner while it is active - your cue it is in control.',
    'ext.browser.note_token':        'To act as you on a site you are signed in to, it may read that page\'s own login token for the task - sent to HQ over an encrypted channel, never to anyone else.',
    'ext.browser.note_detach':       'Nothing runs on its own. Turn it off any time; that detaches immediately.',
    'ext.files.tab_docs':            'Documents',
    'ext.files.tab_gen':             'Generated',
    'ext.files.search_ph':           'Search documents…',
    'ext.files.empty_docs':          'No documents.',
    'ext.files.empty_gen':           'No generated files.',
    'ext.files.del_doc_title':       'Delete document',
    'ext.files.del_doc_msg':         'Delete "{name}"?',
    'ext.files.del_file_title':      'Delete file',
    'ext.files.del_file_msg':        'Delete "{name}"?',
    'ext.files.tab_trash':           'Trash',
    'ext.files.root_private':        'My Documents',
    'ext.files.root_team':           'Team Documents',
    'ext.files.root_channels':       'Channels',
    'ext.files.back':                'Back',
    'ext.files.new_folder_title':    'New folder',
    'ext.files.upload':              'Upload',
    'ext.files.uploading':           'Uploading…',
    'ext.files.folder_name':         'Folder name',
    'ext.files.rename_folder_title': 'Rename folder',
    'ext.files.rename':              'Rename',
    'ext.files.del_folder_title':    'Delete folder',
    'ext.files.del_folder_msg':      'Move "{name}" and its contents to Trash?',
    'ext.files.folders_heading':     'Folders',
    'ext.files.files_heading':       'Files',
    'ext.files.empty_folder':        'Nothing here yet.',
    'ext.files.empty_channels':      'No channel files.',
    'ext.files.move':                'Move',
    'ext.files.move_title':          'Move to…',
    'ext.files.here':                'Here',
    'ext.files.trash_empty':         'Trash is empty.',
    'ext.files.restore':             'Restore',
    'ext.files.delete_forever':      'Delete forever',
    'ext.files.purge_title':         'Delete forever',
    'ext.files.purge_doc_msg':       'Permanently delete "{name}"? This cannot be undone.',
    'ext.files.purge_folder_title':  'Delete folder forever',
    'ext.files.purge_folder_msg':    'Permanently delete "{name}" and its contents?',
    'ext.apps.empty':                'No apps yet.',
    'ext.memory.learned':            'What HQ has learned',
    'ext.memory.empty_facts':        'Nothing yet.',
    'ext.memory.correct':            'Correct',
    'ext.memory.forget':             'Forget',
    'ext.memory.forget_title':       'Forget fact',
    'ext.memory.forget_msg':         'Forget this fact? HQ will stop using it.',
    'ext.memory.access':             'Agent memory access',
    'ext.memory.empty_agents':       'No agents.',
    'ext.memory.frozen':             'Frozen - cannot read your memory',
    'ext.memory.can_read':           'Can read your memory',
    'ext.memory.freeze':             'Freeze',
    'ext.memory.unfreeze':           'Unfreeze',
    'ext.conv.untitled':             'Untitled',
    'ext.conv.open_studio':          'Open in Studio',
    'ext.conv.rename':               'Rename',
    'ext.artifact.image_unavailable':'image unavailable',
    'ext.artifact.image_loading':    'loading image…',

    // ── web-ui HomeView.vue ──
    'home.greeting.morning':         'Good morning',
    'home.greeting.afternoon':       'Good afternoon',
    'home.greeting.evening':         'Good evening',
    'home.hero_sub':                 'What would you like to get done?',
    'home.composer.placeholder_agent':'Message {name}...',
    'home.composer.placeholder_fast':'Ask a quick question...',
    'home.send':                     'Send',
    'home.send_to':                  'Send to',
    'home.quick_ask':                'Quick ask',
    'home.all_agents':               'All agents',
    'home.you':                      'You',
    'home.thinking':                 'Thinking...',
    'home.new':                      'New',
    'home.open_in_studio':           'Open in Studio',
    'home.hint_fast':                "Instant answers, drawing on what your workspace knows. Quick ask can't run tasks, edit files, or use apps - pick an agent for that.",
    'home.hint_agent_before':        'Press',
    'home.hint_agent_mid':           'to start a Studio conversation with',
    'home.hint_agent_after':         '. Agents run tasks, edit files, and use apps.',
    'home.continue':                 'Continue',
    'home.all_threads':              'All threads',
    'home.agents':                   'Agents',
    'home.new_thread':               'New thread',
    'home.default':                  'Default',
    'home.start_conversation':       'Start a conversation',
    'home.learn_title':              'Getting started with HQ',
    'home.learn_text':               'Learn how to ask agents, run tasks, and build apps - browse the help & docs.',
    'home.connect_title':            'Connect a channel',
    'home.connect_text':             "HQ isn't in Slack or Microsoft Teams yet. Add it so your team can work with HQ where they already are.",
    'home.connect_dismiss':          'Dismiss',
    'home.launch_error':             'Could not start that conversation.',
    'home.untitled_thread':          'Untitled thread',
    'home.attach_needs_agent':       'Attachments need an agent - sending to {name}.',
    'home.drop_attach':              'Drop to attach',
    'home.ago_s':                    '{count}s ago',
    'home.ago_min':                  '{count} min ago',
    'home.ago_h':                    '{count} h ago',
    'home.ago_d':                    '{count} d ago',

    // ── web-ui QuickAsk.vue ──
    'quickask.placeholder_agent':    'Message {name}…',
    'quickask.placeholder_fast':     'Ask a quick question…',
    'quickask.quick_ask':            'Quick ask',
    'quickask.send':                 'Send',
    'quickask.send_to':              'Send to',
    'quickask.all_agents':           'All agents',
    'quickask.you':                  'You',
    'quickask.thinking':             'Thinking…',
    'quickask.new':                  'New',
    'quickask.open_in_studio':       'Open in Studio',
    'quickask.hint_fast':            "Instant answers, drawing on what your workspace knows. Quick chat can't run tasks, edit files, or use apps - pick an agent for that.",
    'quickask.hint_agent_before':    'Press',
    'quickask.hint_agent_mid':       'to start a Studio conversation with',
    'quickask.hint_agent_after':     '. Agents run tasks, edit files, and use apps - this opens the full Studio.',
    'quickask.launch_error':         'Could not start that conversation.',
    'quickask.attach_needs_agent':   'Attachments need an agent - sending to {name}.',
    'quickask.drop_attach':          'Drop to attach',

    // ── web-ui ConversationsView.vue ──
    'conversations.title':           'Studio',
    'conversations.lane_aria':       'Conversation lane',
    'conversations.lane_agents':     'Agents',
    'conversations.lane_ask':        'Ask',
    'conversations.lane_email':      'Email',
    'conversations.sort_recent':     'Recent',
    'conversations.sort_oldest':     'Oldest',
    'conversations.sort_az':         'A-Z',
    'conversations.sort_aria':       'Sort order',
    'conversations.search_placeholder':'Search by title, agent, or message',
    'conversations.search_aria':     'Search conversations',
    'conversations.new_ask':         'New ask',
    'conversations.new_thread':      'New thread',
    'conversations.out_of_credits_tip':'Out of HQ credits - upgrade to continue',
    'conversations.out_of_credits_banner':'This workspace is out of HQ credits. New chats are paused until your plan is upgraded.',
    'conversations.upgrade':         'Upgrade',
    'conversations.no_matches_title':'No matches',
    'conversations.no_matches_body': "Nothing matches '{query}'.",
    'conversations.clear_search':    'Clear search',
    'conversations.empty_title':     'No conversations yet',
    'conversations.empty_body':      'Start one from Slack or with New thread.',
    'conversations.empty_ask_title': 'No quick chats yet',
    'conversations.empty_ask_body':  'Quick chats give instant answers with no setup. Convert one to a full agent thread anytime.',
    'conversations.empty_email_title':'No email yet',
    'conversations.empty_email_body':'Email conversations you are part of show up here. New mail to your agents lands automatically.',
    'conversations.start_ask_error': 'Could not start a quick chat',
    'conversations.load_error':      'Failed to load conversations (HTTP {status}): {detail}',
    'conversations.agents_load_error':'Failed to load agents',
    'conversations.create_error':    'Failed to create conversation',
    'conversations.delete_title':    'Delete conversation?',
    'conversations.delete_body':     'This permanently deletes the conversation and its messages. This cannot be undone.',
    'conversations.delete_body_app': 'This permanently deletes the conversation and its messages, and destroys its app "{slug}". This cannot be undone.',
    'conversations.delete_confirm_word':'delete',
    'conversations.delete_confirm_label':'Delete conversation',
    'conversations.delete_files_one':"Also delete this conversation's {count} delivered file (otherwise kept, still downloadable)",
    'conversations.delete_files_other':"Also delete this conversation's {count} delivered files (otherwise kept, still downloadable)",
    'conversations.deleted_toast':   'Conversation deleted',
    'conversations.no_messages':     'No messages yet.',
    'conversations.quick_ask_label': 'Quick ask',
    'conversations.backed_by_app':   'backed by app {slug}',
    'conversations.delete_tip':      'Delete conversation',
    'conversations.delete_aria':     'Delete {name}',
    'conversations.conversation_noun':'conversation',
    'conversations.subtitle_empty':  'No conversations yet.',
    'conversations.subtitle_no_matches':'No matches.',
    'conversations.subtitle_matching':'{count} matching {noun}.',
    'conversations.subtitle_total':  '{count} {noun} across this workspace.',
    'conversations.noun_quick_chat_one':'quick chat',
    'conversations.noun_quick_chat_other':'quick chats',
    'conversations.noun_thread_one': 'thread',
    'conversations.noun_thread_other':'threads',
    'conversations.pager_range':     '{start}-{end} of {total}',
    'conversations.picker_title':    'Pick an agent',
    'conversations.picker_loading':  'Loading agents...',
    'conversations.picker_empty':    'No agents installed yet. Ask a workspace admin to add one.',
    'conversations.ago_s':           '{count}s ago',
    'conversations.ago_min':         '{count} min ago',
    'conversations.ago_h':           '{count} h ago',
    'conversations.ago_d':           '{count} d ago',
    'conversations.usage_tip':       '{new} new + {cache} cached tokens · {turns} turns',

    // ── web-ui InboxView.vue ──
    'inbox.title':                   'Inbox',
    'inbox.mark_all_read':           'Mark all read',
    'inbox.search_placeholder':      'Search messages',
    'inbox.search_aria':             'Search inbox',
    'inbox.filter_all':              'All',
    'inbox.filter_unread':           'Unread',
    'inbox.read_filter_aria':        'Read filter',
    'inbox.sort_newest':             'Newest',
    'inbox.sort_oldest':             'Oldest',
    'inbox.sort_aria':               'Sort order',
    'inbox.untitled':                'Untitled',
    'inbox.scheduled_meta':          '· scheduled',
    'inbox.open_schedule':           'Open schedule: {name}',
    'inbox.mark_read':               'Mark as read',
    'inbox.mark_unread':             'Mark as unread',
    'inbox.delete_title':            'Delete message?',
    'inbox.delete_body':             'This cannot be undone.',
    'inbox.empty_filter_title':      'No matching messages',
    'inbox.empty_filter_body':       'No messages match your current filter. Try a different search or switch back to All.',
    'inbox.empty_title':             'No messages yet',
    'inbox.empty_body':              'Schedules set to deliver to web inbox will land here.',
    'email_replies.title':           'Email replies',
    'email_replies.subtitle':        'Replies to mail the assistant sent from your team address, assigned to you to read. The assistant never acts on these.',
    'email_replies.empty_title':     'Nothing to review',
    'email_replies.empty_body':      'Replies assigned to you show up here for you to read.',
    'email_replies.no_subject':      '(no subject)',
    'email_replies.open':            'Open thread',
    'email_replies.done':            'Done',
    'email_replies.done_toast':      'Marked reviewed.',
    'email_replies.done_err':        'Could not mark it reviewed.',
    'email_replies.tab_open':        'To review',
    'email_replies.tab_reviewed':    'Reviewed',
    'email_replies.tabs_aria':       'Filter email replies',
    'email_replies.empty_reviewed_title': 'Nothing reviewed yet',
    'email_replies.empty_reviewed_body':  'Replies you mark done show up here.',
    'email_replies.reopen':          'Reopen',
    'email_replies.reopen_toast':    'Moved back to review.',
    'email_replies.reopen_err':      'Could not reopen it.',
    'inbox.pager_range':             '{start}-{end} of {total}',
    'inbox.page_of':                 'Page {page} of {total}',
    'inbox.prev_page':               'Previous page',
    'inbox.next_page':               'Next page',

    // Activity - schedule run history (one row per executed fire).
    'activity.title':                'Activity',
    'activity.filter_all':           'All',
    'activity.filter_active':        'Active',
    'activity.filter_failed':        'Failed',
    'activity.filter_aria':          'Run status filter',
    'activity.order_newest':         'Newest',
    'activity.order_oldest':         'Oldest',
    'activity.order_aria':           'Order',
    'activity.col_when':             'Time',
    'activity.col_status':           'Status',
    'activity.col_duration':         'Duration',
    'activity.col_schedule':         'Schedule',
    'activity.today':                'Today',
    'activity.yesterday':            'Yesterday',
    'activity.schedule_filter':      'Runs for {name}',
    'activity.clear_schedule':       'All activity',
    'activity.manual_run':           'Manual run',
    'activity.status.created':       'Starting',
    'activity.status.running':       'Running',
    'activity.status.suspended':     'Waiting',
    'activity.status.completed':     'Completed',
    'activity.status.failed':        'Failed',
    'activity.status.cancelled':     'Cancelled',
    'activity.open_thread':          'Open thread',
    'activity.open_schedule':        'Open schedule',
    'activity.stop':                 'Stop run',
    'activity.stop_tip':             'Stop this run',
    'activity.stop_confirm_title':   'Stop this run?',
    'activity.stop_confirm_body':    'It will be marked cancelled and its pending steps dropped. This cannot be undone.',
    'activity.error_toggle':         'Error details',
    'activity.empty_title':          'No runs yet',
    'activity.empty_body':           'When a schedule fires, each run lands here with its status and timing.',
    'activity.empty_filter_title':   'No matching runs',
    'activity.empty_filter_body':    'No runs match this filter.',
    'activity.pager_range':          '{start}-{end} of {total}',
    'activity.page_of':              'Page {page} of {total}',
    'activity.prev_page':            'Previous page',
    'activity.next_page':            'Next page',
  },
  sv: {
    'studio.pending.default':   'Tänker',
    'studio.pending.early':     'Tänker',
    'studio.pending.mid':       'Arbetar',
    'studio.pending.long':      'Arbetar fortfarande',
    'studio.pending.very_long': 'Strax klart',

    'studio.phase.starting':    'Startar',
    'studio.phase.acquiring':   'Förbereder',
    'studio.phase.warm_adopt':  'Förbereder',
    'studio.phase.restoring':   'Återupptar',
    'studio.phase.cold_start':  'Startar upp',
    'studio.phase.vm_ready':    'Snart klar',

    'studio.tools.calling':     'Använder {name}',
    'studio.tools.used_one':    '1 verktyg använt',
    'studio.tools.used_other':  '{count} verktyg använda',
    'studio.tools.copy':        'Kopiera',
    'studio.tools.copied':      'Kopierat',
    'studio.tools.label_args':  'Argument',
    'studio.tools.label_result':'Resultat',

    'studio.share.button':           'Dela',
    'studio.share.button_title':     'Dela {slug}',
    'studio.share.title':            'Dela den här appen',
    'studio.share.close':            'Stäng',
    'studio.share.cancel':           'Avbryt',
    'studio.share.copy_link':        'Kopiera länk',
    'studio.share.copied':           'Kopierat',
    'studio.share.mode_expiring':    'Personer jag bjuder in',
    'studio.share.mode_public':      'Alla med länken',
    'studio.share.expiring_hint':    'En signerad länk som släpper in en person. Välj hur länge den gäller.',
    'studio.share.ttl_label':        'Länken slutar gälla om',
    'studio.share.ttl_1h':           '1 timme',
    'studio.share.ttl_24h':          '24 timmar',
    'studio.share.ttl_7d':           '7 dagar',
    'studio.share.ttl_30d':          '30 dagar',
    'studio.share.public_warn_title':'Gör appen offentlig?',
    'studio.share.public_warn_body': 'Vem som helst med länken kan öppna appen, utan inloggning och utan utgångsdatum. Du kan när som helst växla tillbaka till inbjudan-bara.',
    'studio.share.make_public':      'Gör offentlig',
    'studio.share.make_private':     'Växla till inbjudan-bara',
    'studio.share.public_hint':      'Alla med den här länken kan öppna appen.',
    'studio.share.clipboard_unavailable': 'Urklipp ej tillgängligt - markera URL:en manuellt.',

    // Generic words reused across many views. Prefer these over per-view copies.
    'common.back':                   'Tillbaka',
    'common.save':                   'Spara',
    'common.saving':                 'Sparar…',
    'common.cancel':                 'Avbryt',
    'common.close':                  'Stäng',
    'common.delete':                 'Ta bort',
    'common.remove':                 'Ta bort',
    'common.edit':                   'Redigera',
    'common.loading':                'Laddar…',
    'common.retry':                  'Försök igen',
    'common.confirm':                'Bekräfta',
    'common.copy':                   'Kopiera',
    'common.copied':                 'Kopierat',
    'common.search':                 'Sök',
    'common.name':                   'Namn',
    'common.description':            'Beskrivning',
    'common.enabled':                'Aktiverad',
    'common.disabled':               'Inaktiverad',
    'common.yes':                    'Ja',
    'common.no':                     'Nej',
    'common.done':                   'Klar',
    'common.next':                   'Nästa',
    'common.dismiss':                'Avfärda',
    'common.error_generic':          'Något gick fel',
    'mermaid.render_failed':         'Diagrammet kunde inte renderas. Visar källkoden.',

    // Toast notifications (web-ui ToastStack).
    'toast.region_label':            'Aviseringar',

    // Extension "More" screen (apps/extension/components/MoreView.vue).
    'more.title':                    'Mer',
    'more.about.title':              'Om mig',
    'more.about.sub':                'Vad HQ minns om dig',
    'more.files.title':              'Filer',
    'more.files.sub':                'Dokument och genererade filer',
    'more.apps.title':               'Appar',
    'more.apps.sub':                 'Appar byggda av agenter',
    'more.browser.title':            'Webbläsarstyrning',
    'more.browser.sub':              'Låt agenter styra den här webbläsaren',
    'more.selection.title':          'Markeringsverktyg',
    'more.selection.sub':            'Snabbåtgärder när du markerar text på en sida',
    'more.web.title':                'Öppna HQ på webben',
    'more.web.sub':                  'Hela appen i din webbläsare',
    'more.signout.title':            'Logga ut',
    'more.signout.sub':              'Avsluta din session på den här enheten',

    // Sign-in surface (apps/web-ui/src/views/SigninView.vue).
    'signin.title':                  'Logga in på HQ',
    'signin.sub':                    'Använd arbetsytan där du har HQ installerat. Vi ber inte om något separat lösenord.',
    'signin.slack.title':            'Logga in med Slack',
    'signin.slack.redirecting':      'Omdirigerar…',
    'signin.slack.sub':              'Använd din befintliga arbetsyteidentitet',
    'signin.teams.title':            'Logga in med Microsoft',
    'signin.teams.sub':              'Använd ditt Microsoft-arbetskonto',
    'signin.teams.redirecting':      'Omdirigerar…',
    'signin.divider':                'eller med e-post',
    'signin.email.aria':             'E-postadress',
    'signin.email.sending':          'Skickar…',
    'signin.email.cta':              'Mejla mig en inloggningslänk',
    'signin.email.sub':              'För gäster och inbjudna medlemmar utan Slack',
    'signin.magic_sent_before':      'Om',
    'signin.magic_sent_after':       'har åtkomst är en inloggningslänk på väg. Kolla din e-post.',
    'signin.code.cta':               'Ange din kod',
    'signin.code.sub':               'Fick du koden via e-post? Skriv in den här för att logga in på den här enheten.',
    'signin.code.aria':              'Inloggningskod',
    'signin.code.verifying':         'Kontrollerar…',
    'signin.code.err':               'Den koden fungerade inte. Kolla din e-post och försök igen.',
    'signin.err.slack_start':        'Slack-inloggningen kunde inte startas',
    'signin.err.teams_start':        'Microsoft-inloggningen kunde inte startas',
    'signin.err.invalid_email':      'Ange en giltig e-postadress.',
    'signin.pw.toggle':              'Logga in med lösenord',
    'signin.pw.back':                'Tillbaka till inloggningsalternativ',
    'signin.pw.sub':                 'Ange din konto-e-post och ditt lösenord.',
    'signin.pw.email_aria':          'E-postadress',
    'signin.pw.password_aria':       'Lösenord',
    'signin.pw.password_ph':         'Lösenord',
    'signin.pw.cta':                 'Logga in',
    'signin.pw.signing':             'Loggar in…',
    'signin.pw.choose_title':        'Välj en arbetsyta',
    'signin.pw.err.required':        'Ange din e-post och ditt lösenord.',
    'signin.pw.err.invalid':         'Felaktig e-post eller lösenord.',
    'signin.pw.err.throttled':       'För många inloggningsförsök. Försök igen senare.',
    'signin.aside.eyebrow':          'Ny på HQ',
    'signin.aside.title':            'Specialister som gör jobbet, inte bara chattar',
    'signin.aside.lede':             '@nämn en specialist i Slack eller Teams så gör den research, beslutar och levererar - rapporter, dokument, e-post, scheman.',
    'signin.aside.point1':           'Lever i Slack och Teams, inte ännu en flik',
    'signin.aside.point2':           'Gör riktigt arbete: research, dokument, e-post, scheman',
    'signin.aside.point3':           'Kommer ihåg och lär av arbetet',
    'signin.aside.cta':              'Skapa en arbetsyta',
    'signin.aside.note':             'Inbjudan krävs under betan.',

    // ── Extension side panel (apps/extension/entrypoints/sidepanel/App.vue) ──
    'ext.tab.ask':                   'Fråga',
    'ext.tab.agents':                'Agenter',
    'ext.signin.sub':                'Logga in på din arbetsyta.',
    'ext.signin.button':             'Logga in på HQ',
    'ext.signin.new_before':         'Ny på HQ?',
    'ext.signin.get_started':        'Kom igång →',
    'ext.signin.device_title':       'Godkänn inloggning',
    'ext.signin.device_lede':        'Vi öppnade en verifieringssida. Bekräfta den här koden där:',
    'ext.signin.device_open':        'Öppna verifieringssidan igen',
    'ext.signin.device_waiting':     'Väntar på godkännande…',
    'ext.signin.device_cancel':      'Avbryt',
    'ext.ws.title':                  'Arbetsyta: {name}',
    'ext.stale.message':             'HQ har uppdaterats. Ladda om för att slutföra.',
    'ext.stale.reload':              'Ladda om',
    'ext.ask.quick_ask':             'Snabbfråga',
    'ext.ask.search':                'Sök snabbfrågor…',
    'ext.ask.recent':                'Senaste',
    'ext.agents.choose':             'Välj en agent',
    'ext.agents.search':             'Sök konversationer…',
    'ext.agents.choose_agent':       'Välj agent',
    'ext.agents.an_agent':           'en agent',
    'ext.agents.send_to':            'Skicka till {name}',
    'ext.composer.ask_anything':     'Fråga vad som helst…',
    'ext.composer.message_agent':    'Skriv till agenten…',
    'ext.composer.ask_agent':        'Fråga {name}…',
    'ext.composer.attach':           'Bifoga filer',
    'ext.composer.uploading':        'laddar upp…',
    'ext.composer.stop':             'Stoppa agenten',
    'ext.composer.nudge':            'Puffa',
    'ext.composer.send':             'Skicka',
    'ext.empty.ask':                 'Fråga vad som helst - snabbt, utan sandbox.',
    'ext.empty.ask_agent':           'Fråga {name} vad som helst.',
    'ext.empty.send_to_start':       'Skicka ett meddelande för att komma igång.',
    'ext.empty.no_matches':          'Inga träffar.',
    'ext.empty.no_threads':          'Inga agenttrådar än.',
    'ext.loading_more':              'Laddar mer…',
    'ext.tool.calling_before':       'Anropar ',
    'ext.tool.calling_after':        '…',
    'ext.tool.no_details':           'Inga detaljer.',
    'ext.notice.context_full':       'Den här snabbfrågan är full.',
    'ext.notice.convert':            'Gör om till en agenttråd →',
    'ext.drop.thread':               'Släpp för att bifoga i den här tråden',
    'ext.drop.new_thread':           'Släpp för att bifoga i en ny agenttråd',
    'ext.readonly.slack':            'Sker i Slack - svara i kanalen.',
    'ext.browser.use':               'Använd min webbläsare',
    'ext.browser.acting':            'HQ agerar på sidan…',
    'ext.browser.controlling':       'Styr din aktiva webbläsare - klicka för att stoppa och koppla från',
    'ext.browser.connecting':        'Ansluter till din webbläsare…',
    'ext.browser.enable_hint':       'Låt agenter styra din aktiva webbläsare - ansluter tillägget och kör på sidan du tittar på',
    'ext.browser.warn_title':        'HQ kan nu agera i din webbläsare.',
    'ext.browser.warn_body':         'Den kan läsa och utföra åtgärder på sidorna du öppnar, vilket kan utsätta dina data för risk. Inaktivera när du är klar.',
    'ext.browser.deactivate':        'Inaktivera',
    'ext.confirm.delete_title':      'Ta bort konversation',
    'ext.confirm.delete_msg':        'Ta bort "{title}"? Detta kan inte ångras.',
    'ext.confirm.delete_fallback':   'den här konversationen',
    'ext.sender.you':                'Du',
    'ext.sender.assistant':          'Assistent',
    'ext.err.no_agent':              'Ingen agent vald.',
    'ext.err.upload_failed':         'Uppladdningen misslyckades: {error}',
    'ext.err.agent':                 'agentfel',
    'ext.cmd.placeholder':           'Sök eller kör ett kommando…',
    'ext.cmd.new_ask':               'Ny snabbfråga',
    'ext.cmd.new_thread':            'Ny agenttråd',
    'ext.cmd.browser_on':            'Slå på "Använd min webbläsare"',
    'ext.cmd.browser_off':           'Stäng av "Använd min webbläsare"',
    'ext.cmd.open_studio':           'Öppna i Studio',
    'ext.cmd.settings':              'Inställningar',
    'ext.cmd.searching':             'Söker…',
    'ext.cmd.untitled':              'Namnlös',
    'ext.cmd.message_agent':         'Skriv till {name}',
    'ext.cmd.group_actions':         'Åtgärder',
    'ext.cmd.group_agents':          'Agenter',
    'ext.cmd.group_view':            'Visa',
    'ext.cmd.group_switch_ws':       'Byt arbetsyta',
    'ext.cmd.group_account':         'Konto',
    'ext.cmd.group_conversations':   'Konversationer',

    // ── Extension components ──
    'ext.browser.title':             'Låt agenter styra den här webbläsaren',
    'ext.browser.status_connected':  'Ansluten',
    'ext.browser.status_connecting': 'Ansluter…',
    'ext.browser.status_waiting':    'Väntar på anslutning',
    'ext.browser.status_off':        'Av',
    'ext.browser.lead':              'En agent du ber kan läsa sidan, klicka, skriva och navigera bland dina egna inloggade flikar - och bara när du ber den om det.',
    'ext.browser.note_banner':       'Chrome visar en banner om att webbläsaren felsöks medan den är aktiv - din signal om att den har kontrollen.',
    'ext.browser.note_token':        'För att agera som du på en webbplats du är inloggad på kan den läsa den sidans egen inloggningstoken för uppgiften - skickad till HQ via en krypterad kanal, aldrig till någon annan.',
    'ext.browser.note_detach':       'Inget körs på egen hand. Stäng av den när som helst; den kopplas då bort omedelbart.',
    'ext.files.tab_docs':            'Dokument',
    'ext.files.tab_gen':             'Genererade',
    'ext.files.search_ph':           'Sök dokument…',
    'ext.files.empty_docs':          'Inga dokument.',
    'ext.files.empty_gen':           'Inga genererade filer.',
    'ext.files.del_doc_title':       'Ta bort dokument',
    'ext.files.del_doc_msg':         'Ta bort "{name}"?',
    'ext.files.del_file_title':      'Ta bort fil',
    'ext.files.del_file_msg':        'Ta bort "{name}"?',
    'ext.files.tab_trash':           'Papperskorg',
    'ext.files.root_private':        'Mina dokument',
    'ext.files.root_team':           'Teamdokument',
    'ext.files.root_channels':       'Kanaler',
    'ext.files.back':                'Tillbaka',
    'ext.files.new_folder_title':    'Ny mapp',
    'ext.files.upload':              'Ladda upp',
    'ext.files.uploading':           'Laddar upp…',
    'ext.files.folder_name':         'Mappnamn',
    'ext.files.rename_folder_title': 'Byt namn på mapp',
    'ext.files.rename':              'Byt namn',
    'ext.files.del_folder_title':    'Ta bort mapp',
    'ext.files.del_folder_msg':      'Flytta "{name}" och dess innehåll till papperskorgen?',
    'ext.files.folders_heading':     'Mappar',
    'ext.files.files_heading':       'Filer',
    'ext.files.empty_folder':        'Inget här än.',
    'ext.files.empty_channels':      'Inga kanalfiler.',
    'ext.files.move':                'Flytta',
    'ext.files.move_title':          'Flytta till…',
    'ext.files.here':                'Här',
    'ext.files.trash_empty':         'Papperskorgen är tom.',
    'ext.files.restore':             'Återställ',
    'ext.files.delete_forever':      'Ta bort permanent',
    'ext.files.purge_title':         'Ta bort permanent',
    'ext.files.purge_doc_msg':       'Ta bort "{name}" permanent? Detta kan inte ångras.',
    'ext.files.purge_folder_title':  'Ta bort mapp permanent',
    'ext.files.purge_folder_msg':    'Ta bort "{name}" och dess innehåll permanent?',
    'ext.apps.empty':                'Inga appar än.',
    'ext.memory.learned':            'Vad HQ har lärt sig',
    'ext.memory.empty_facts':        'Inget än.',
    'ext.memory.correct':            'Korrigera',
    'ext.memory.forget':             'Glöm',
    'ext.memory.forget_title':       'Glöm faktum',
    'ext.memory.forget_msg':         'Glöm det här faktumet? HQ slutar använda det.',
    'ext.memory.access':             'Agenters minnesåtkomst',
    'ext.memory.empty_agents':       'Inga agenter.',
    'ext.memory.frozen':             'Fryst - kan inte läsa ditt minne',
    'ext.memory.can_read':           'Kan läsa ditt minne',
    'ext.memory.freeze':             'Frys',
    'ext.memory.unfreeze':           'Tina',
    'ext.conv.untitled':             'Namnlös',
    'ext.conv.open_studio':          'Öppna i Studio',
    'ext.conv.rename':               'Byt namn',
    'ext.artifact.image_unavailable':'bilden är inte tillgänglig',
    'ext.artifact.image_loading':    'laddar bild…',

    // ── web-ui HomeView.vue ──
    'home.greeting.morning':         'God morgon',
    'home.greeting.afternoon':       'God eftermiddag',
    'home.greeting.evening':         'God kväll',
    'home.hero_sub':                 'Vad vill du få gjort?',
    'home.composer.placeholder_agent':'Meddela {name}...',
    'home.composer.placeholder_fast':'Ställ en snabb fråga...',
    'home.send':                     'Skicka',
    'home.send_to':                  'Skicka till',
    'home.quick_ask':                'Snabbfråga',
    'home.all_agents':               'Alla agenter',
    'home.you':                      'Du',
    'home.thinking':                 'Tänker...',
    'home.new':                      'Ny',
    'home.open_in_studio':           'Öppna i Studio',
    'home.hint_fast':                'Direkta svar utifrån vad din arbetsyta vet. Snabbfrågor kan inte köra uppgifter, redigera filer eller använda appar - välj en agent för det.',
    'home.hint_agent_before':        'Tryck',
    'home.hint_agent_mid':           'för att starta en Studio-konversation med',
    'home.hint_agent_after':         '. Agenter kör uppgifter, redigerar filer och använder appar.',
    'home.continue':                 'Fortsätt',
    'home.all_threads':              'Alla trådar',
    'home.agents':                   'Agenter',
    'home.new_thread':               'Ny tråd',
    'home.default':                  'Standard',
    'home.start_conversation':       'Starta en konversation',
    'home.learn_title':              'Kom igång med HQ',
    'home.learn_text':               'Lär dig att fråga agenter, köra uppgifter och bygga appar - läs hjälp & dokumentation.',
    'home.connect_title':            'Anslut en kanal',
    'home.connect_text':             'HQ finns inte i Slack eller Microsoft Teams ännu. Lägg till det så att teamet kan arbeta med HQ där de redan är.',
    'home.connect_dismiss':          'Avfärda',
    'home.launch_error':             'Det gick inte att starta den konversationen.',
    'home.untitled_thread':          'Namnlös tråd',
    'home.attach_needs_agent':       'Bilagor behöver en agent - skickar till {name}.',
    'home.drop_attach':              'Släpp för att bifoga',
    'home.ago_s':                    'för {count}s sedan',
    'home.ago_min':                  'för {count} min sedan',
    'home.ago_h':                    'för {count} tim sedan',
    'home.ago_d':                    'för {count} d sedan',

    // ── web-ui QuickAsk.vue ──
    'quickask.placeholder_agent':    'Meddela {name}…',
    'quickask.placeholder_fast':     'Ställ en snabb fråga…',
    'quickask.quick_ask':            'Snabbfråga',
    'quickask.send':                 'Skicka',
    'quickask.send_to':              'Skicka till',
    'quickask.all_agents':           'Alla agenter',
    'quickask.you':                  'Du',
    'quickask.thinking':             'Tänker…',
    'quickask.new':                  'Ny',
    'quickask.open_in_studio':       'Öppna i Studio',
    'quickask.hint_fast':            'Direkta svar utifrån vad din arbetsyta vet. Snabbchatten kan inte köra uppgifter, redigera filer eller använda appar - välj en agent för det.',
    'quickask.hint_agent_before':    'Tryck',
    'quickask.hint_agent_mid':       'för att starta en Studio-konversation med',
    'quickask.hint_agent_after':     '. Agenter kör uppgifter, redigerar filer och använder appar - detta öppnar hela Studio.',
    'quickask.launch_error':         'Det gick inte att starta den konversationen.',
    'quickask.attach_needs_agent':   'Bilagor behöver en agent - skickar till {name}.',
    'quickask.drop_attach':          'Släpp för att bifoga',

    // ── web-ui ConversationsView.vue ──
    'conversations.title':           'Studio',
    'conversations.lane_aria':       'Konversationsspår',
    'conversations.lane_agents':     'Agenter',
    'conversations.lane_ask':        'Fråga',
    'conversations.lane_email':      'E-post',
    'conversations.sort_recent':     'Senaste',
    'conversations.sort_oldest':     'Äldsta',
    'conversations.sort_az':         'A-Ö',
    'conversations.sort_aria':       'Sorteringsordning',
    'conversations.search_placeholder':'Sök på titel, agent eller meddelande',
    'conversations.search_aria':     'Sök konversationer',
    'conversations.new_ask':         'Ny fråga',
    'conversations.new_thread':      'Ny tråd',
    'conversations.out_of_credits_tip':'Slut på HQ-krediter - uppgradera för att fortsätta',
    'conversations.out_of_credits_banner':'Den här arbetsytan har slut på HQ-krediter. Nya chattar är pausade tills din plan uppgraderas.',
    'conversations.upgrade':         'Uppgradera',
    'conversations.no_matches_title':'Inga träffar',
    'conversations.no_matches_body': "Inget matchar '{query}'.",
    'conversations.clear_search':    'Rensa sökning',
    'conversations.empty_title':     'Inga konversationer än',
    'conversations.empty_body':      'Starta en från Slack eller med Ny tråd.',
    'conversations.empty_ask_title': 'Inga snabbchattar än',
    'conversations.empty_ask_body':  'Snabbchattar ger direkta svar utan någon uppsättning. Konvertera en till en full agenttråd när som helst.',
    'conversations.empty_email_title':'Ingen e-post än',
    'conversations.empty_email_body':'E-postkonversationer du är en del av visas här. Ny e-post till dina agenter landar automatiskt.',
    'conversations.start_ask_error': 'Det gick inte att starta en snabbchatt',
    'conversations.load_error':      'Det gick inte att läsa in konversationer (HTTP {status}): {detail}',
    'conversations.agents_load_error':'Det gick inte att läsa in agenter',
    'conversations.create_error':    'Det gick inte att skapa konversationen',
    'conversations.delete_title':    'Ta bort konversation?',
    'conversations.delete_body':     'Detta tar permanent bort konversationen och dess meddelanden. Detta kan inte ångras.',
    'conversations.delete_body_app': 'Detta tar permanent bort konversationen och dess meddelanden, och förstör dess app "{slug}". Detta kan inte ångras.',
    'conversations.delete_confirm_word':'ta bort',
    'conversations.delete_confirm_label':'Ta bort konversation',
    'conversations.delete_files_one':'Ta även bort den här konversationens {count} levererade fil (annars behålls den, fortfarande nedladdningsbar)',
    'conversations.delete_files_other':'Ta även bort den här konversationens {count} levererade filer (annars behålls de, fortfarande nedladdningsbara)',
    'conversations.deleted_toast':   'Konversationen togs bort',
    'conversations.no_messages':     'Inga meddelanden än.',
    'conversations.quick_ask_label': 'Snabbfråga',
    'conversations.backed_by_app':   'backas av appen {slug}',
    'conversations.delete_tip':      'Ta bort konversation',
    'conversations.delete_aria':     'Ta bort {name}',
    'conversations.conversation_noun':'konversation',
    'conversations.subtitle_empty':  'Inga konversationer än.',
    'conversations.subtitle_no_matches':'Inga träffar.',
    'conversations.subtitle_matching':'{count} matchande {noun}.',
    'conversations.subtitle_total':  '{count} {noun} i den här arbetsytan.',
    'conversations.noun_quick_chat_one':'snabbchatt',
    'conversations.noun_quick_chat_other':'snabbchattar',
    'conversations.noun_thread_one': 'tråd',
    'conversations.noun_thread_other':'trådar',
    'conversations.pager_range':     '{start}-{end} av {total}',
    'conversations.picker_title':    'Välj en agent',
    'conversations.picker_loading':  'Läser in agenter...',
    'conversations.picker_empty':    'Inga agenter installerade än. Be en arbetsyteadministratör lägga till en.',
    'conversations.ago_s':           'för {count}s sedan',
    'conversations.ago_min':         'för {count} min sedan',
    'conversations.ago_h':           'för {count} tim sedan',
    'conversations.ago_d':           'för {count} d sedan',
    'conversations.usage_tip':       '{new} nya + {cache} cachade tokens · {turns} turer',

    // ── web-ui InboxView.vue ──
    'inbox.title':                   'Inkorg',
    'inbox.mark_all_read':           'Markera alla som lästa',
    'inbox.search_placeholder':      'Sök meddelanden',
    'inbox.search_aria':             'Sök i inkorgen',
    'inbox.filter_all':              'Alla',
    'inbox.filter_unread':           'Olästa',
    'inbox.read_filter_aria':        'Läsfilter',
    'inbox.sort_newest':             'Nyaste',
    'inbox.sort_oldest':             'Äldsta',
    'inbox.sort_aria':               'Sorteringsordning',
    'inbox.untitled':                'Namnlös',
    'inbox.scheduled_meta':          '· schemalagt',
    'inbox.open_schedule':           'Öppna schema: {name}',
    'inbox.mark_read':               'Markera som läst',
    'inbox.mark_unread':             'Markera som oläst',
    'inbox.delete_title':            'Ta bort meddelande?',
    'inbox.delete_body':             'Detta kan inte ångras.',
    'inbox.empty_filter_title':      'Inga matchande meddelanden',
    'inbox.empty_filter_body':       'Inga meddelanden matchar ditt nuvarande filter. Prova en annan sökning eller växla tillbaka till Alla.',
    'inbox.empty_title':             'Inga meddelanden än',
    'inbox.empty_body':              'Scheman som är inställda på leverans till webbinkorgen landar här.',
    'email_replies.title':           'E-postsvar',
    'email_replies.subtitle':        'Svar på e-post som assistenten skickat från er teamadress, tilldelade dig att läsa. Assistenten agerar aldrig på dessa.',
    'email_replies.empty_title':     'Inget att granska',
    'email_replies.empty_body':      'Svar som tilldelats dig visas här för dig att läsa.',
    'email_replies.no_subject':      '(inget ämne)',
    'email_replies.open':            'Öppna tråd',
    'email_replies.done':            'Klar',
    'email_replies.done_toast':      'Markerat som granskat.',
    'email_replies.done_err':        'Kunde inte markera som granskat.',
    'email_replies.tab_open':        'Att granska',
    'email_replies.tab_reviewed':    'Granskade',
    'email_replies.tabs_aria':       'Filtrera e-postsvar',
    'email_replies.empty_reviewed_title': 'Inget granskat än',
    'email_replies.empty_reviewed_body':  'Svar du markerar som klara visas här.',
    'email_replies.reopen':          'Återöppna',
    'email_replies.reopen_toast':    'Flyttad tillbaka till granskning.',
    'email_replies.reopen_err':      'Kunde inte återöppna.',
    'inbox.pager_range':             '{start}-{end} av {total}',
    'inbox.page_of':                 'Sida {page} av {total}',
    'inbox.prev_page':               'Föregående sida',
    'inbox.next_page':               'Nästa sida',

    // Activity - schedule run history (one row per executed fire).
    'activity.title':                'Aktivitet',
    'activity.filter_all':           'Alla',
    'activity.filter_active':        'Aktiva',
    'activity.filter_failed':        'Misslyckade',
    'activity.filter_aria':          'Statusfilter för körningar',
    'activity.order_newest':         'Nyaste',
    'activity.order_oldest':         'Äldsta',
    'activity.order_aria':           'Sortering',
    'activity.col_when':             'Tid',
    'activity.col_status':           'Status',
    'activity.col_duration':         'Varaktighet',
    'activity.col_schedule':         'Schema',
    'activity.today':                'Idag',
    'activity.yesterday':            'Igår',
    'activity.schedule_filter':      'Körningar för {name}',
    'activity.clear_schedule':       'All aktivitet',
    'activity.manual_run':           'Manuell körning',
    'activity.status.created':       'Startar',
    'activity.status.running':       'Körs',
    'activity.status.suspended':     'Väntar',
    'activity.status.completed':     'Klar',
    'activity.status.failed':        'Misslyckades',
    'activity.status.cancelled':     'Avbruten',
    'activity.open_thread':          'Öppna tråd',
    'activity.open_schedule':        'Öppna schema',
    'activity.stop':                 'Stoppa körning',
    'activity.stop_tip':             'Stoppa den här körningen',
    'activity.stop_confirm_title':   'Stoppa körningen?',
    'activity.stop_confirm_body':    'Den markeras som avbruten och väntande steg tas bort. Detta kan inte ångras.',
    'activity.error_toggle':         'Felinformation',
    'activity.empty_title':          'Inga körningar än',
    'activity.empty_body':           'När ett schema körs hamnar varje körning här med status och tidpunkt.',
    'activity.empty_filter_title':   'Inga matchande körningar',
    'activity.empty_filter_body':    'Inga körningar matchar filtret.',
    'activity.pager_range':          '{start}-{end} av {total}',
    'activity.page_of':              'Sida {page} av {total}',
    'activity.prev_page':            'Föregående sida',
    'activity.next_page':            'Nästa sida',
  },
};

// Final table: the inline `base` plus every area fragment. Areas use disjoint
// key namespaces, so spread order is not significant. (ADD FRAGMENT SPREADS HERE)
export const Strings: Record<LocaleCode, Record<string, string>> = {
  en: {
    ...base.en,
    ...agentsStrings.en,
    ...skillsStrings.en,
    ...appsDocsStrings.en,
    ...schedulesNotifStrings.en,
    ...onboardingAuthStrings.en,
    ...studioMeStrings.en,
    ...adminCoreStrings.en,
    ...adminCommsStrings.en,
    ...componentsStrings.en,
    ...componentsMiscStrings.en,
    ...shellStrings.en,
    ...tablesStrings.en,
  },
  sv: {
    ...base.sv,
    ...agentsStrings.sv,
    ...skillsStrings.sv,
    ...appsDocsStrings.sv,
    ...schedulesNotifStrings.sv,
    ...onboardingAuthStrings.sv,
    ...studioMeStrings.sv,
    ...adminCoreStrings.sv,
    ...adminCommsStrings.sv,
    ...componentsStrings.sv,
    ...componentsMiscStrings.sv,
    ...shellStrings.sv,
    ...tablesStrings.sv,
  },
};
